import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { useAuth } from '../auth/AuthContext';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createOrganizationSchema, createSoftwareApplicationSchema, createWebSiteSchema, createItemListSchema } from '../../components/SEO/StructuredData';

export const LandingPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const workspaceLink = user ? '/workspace' : '/signup';

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  // Handle hash navigation when page loads or hash changes
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'Simcarlo - Monte Carlo Simulation Made Simple',
          description: 'Visual Monte Carlo profitability modeling. Build financial models visually, test thousands of scenarios, and compare outcomes with confidence.',
          keywords: ['Monte Carlo simulation', 'financial modeling', 'risk analysis', 'uncertainty modeling', 'business simulation', 'financial planning'],
          ogImage: `${baseUrl}/images/Social Sharing.png`,
          canonicalUrl: `${baseUrl}/`,
        }}
      />
      <StructuredData
        schemas={[
          createOrganizationSchema('Simcarlo', baseUrl, {
            logo: `${baseUrl}/images/Simcarlo Logo.png`,
            description: 'Visual Monte Carlo profitability modeling for decisions under uncertainty',
            email: 'hello@simcarlo.com',
          }),
          createSoftwareApplicationSchema(
            'Simcarlo',
            'Visual Monte Carlo profitability modeling platform for financial modeling and risk analysis. Build models, test scenarios, and make data-driven decisions.',
            baseUrl,
            {
              applicationCategory: 'BusinessApplication',
              operatingSystem: 'Web Browser',
              offers: [
                {
                  price: '0',
                  priceCurrency: 'EUR',
                  availability: 'https://schema.org/InStock',
                },
                {
                  price: '15',
                  priceCurrency: 'EUR',
                  availability: 'https://schema.org/InStock',
                },
              ],
              screenshot: `${baseUrl}/images/Landing Main.png`,
              // Add ratings when available
              // aggregateRating: {
              //   '@type': 'AggregateRating',
              //   ratingValue: '4.8',
              //   ratingCount: '150',
              // },
            }
          ),
          createWebSiteSchema('Simcarlo', baseUrl),
          createItemListSchema(
            'Simcarlo Key Features',
            [
              { 
                name: 'AI Assistant', 
                description: 'Build models with natural language. Tell the AI what you want in plain English and it builds the model for you.' 
              },
              { 
                name: 'Scenario Results', 
                description: 'Run Monte Carlo trials and inspect output ranges as your model changes.' 
              },
              { 
                name: 'Time Travel', 
                description: 'Undo/redo any change. Save versions like Git commits. Compare scenarios side-by-side.' 
              },
              { 
                name: 'Model Export', 
                description: 'Export results to Excel or complete simulation models to JSON for review and reuse.' 
              },
              { 
                name: 'Visual Modeling', 
                description: 'Build models visually with drag-and-drop nodes. No spreadsheets or coding required.' 
              },
              { 
                name: 'Probability Distributions', 
                description: 'See the full range of possible outcomes with probability distributions, not just single values.' 
              },
            ],
            'Key features that make Simcarlo the best tool for Monte Carlo simulation and financial modeling'
          ),
        ]}
      />
      <MarketingHeader />

      <main>
      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div className="marketing-hero-content">
            <div className="marketing-hero-text">
              <h1 className="marketing-hero-title">Visual Monte Carlo profitability modeling.</h1>
              <p className="marketing-hero-subtitle">Build financial models visually. Test thousands of scenarios. Compare outcomes with confidence.</p>
              <p className="marketing-hero-description">Simcarlo turns uncertain inputs, formulas, and scenarios into clear decision ranges without spreadsheet fragility.</p>
              <div className="marketing-hero-cta">
                <Link to={workspaceLink} className="marketing-btn marketing-btn-primary marketing-btn-large">Open Workspace</Link>
              </div>
              <p className="marketing-hero-note">No credit card required • Free forever plan</p>
            </div>
            <div className="marketing-hero-image">
              <img 
                src="/images/Landing Main.png" 
                alt="Interactive Workspace Screenshot - Visual graph editor with nodes and connections" 
                className="marketing-hero-img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases - Jobs to be Done */}
      <section id="use-cases" className="marketing-use-cases">
        <div className="marketing-container">
          <h2 className="marketing-section-title">If You Can't Afford to Guess</h2>
          <div className="marketing-use-cases-grid">
            <div className="marketing-use-case-card">
              <h3>Financial Planning</h3>
              <p>Model revenue scenarios with uncertain conversion rates, pricing, and market conditions.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">Visual</div>
                  <div className="marketing-stat-label">Map drivers and outcomes</div>
                </div>
              </div>
            </div>
            <div className="marketing-use-case-card">
              <h3>Project Estimation</h3>
              <p>Calculate project costs and timelines when effort, resources, and scope are uncertain.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">Confidently</div>
                  <div className="marketing-stat-label">Make decisions under uncertainty</div>
                </div>
              </div>
            </div>
            <div className="marketing-use-case-card">
              <h3>Investment Analysis</h3>
              <p>Evaluate ROI across multiple scenarios. See risk-adjusted returns, not just best-case.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">Ranges</div>
                  <div className="marketing-stat-label">Compare best, base, and downside cases</div>
                </div>
              </div>
            </div>
            <div className="marketing-use-case-card">
              <h3>Supply Chain Planning</h3>
              <p>Model costs and logistics with variable demand, lead times, and supplier reliability.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">Linked</div>
                  <div className="marketing-stat-label">Trace assumptions through the model</div>
                </div>
              </div>
            </div>
            <div className="marketing-use-case-card">
              <h3>Pricing Strategy</h3>
              <p>Test pricing models against uncertain costs, volumes, and competitive responses.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">Fast</div>
                  <div className="marketing-stat-label">Run and compare scenario ranges</div>
                </div>
              </div>
            </div>
            <div className="marketing-use-case-card">
              <h3>Risk Assessment</h3>
              <p>Quantify risks across dimensions. See probability distributions, not binary outcomes.</p>
              <div className="marketing-use-case-stats">
                <div className="marketing-stat">
                  <div className="marketing-stat-value">P10-P90</div>
                  <div className="marketing-stat-label">Full range analysis</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Downsides of Spreadsheets */}
      <section className="marketing-problem">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Your Spreadsheet Can't Handle Uncertainty</h2>
          <div className="marketing-problem-grid">
            <div className="marketing-problem-card">
              <h3>Single-Point Estimates</h3>
              <p>Spreadsheets show you one outcome. Reality has thousands.</p>
            </div>
            <div className="marketing-problem-card">
              <h3>Fragile Dependencies</h3>
              <p>One broken formula cascades through your entire model.</p>
            </div>
            <div className="marketing-problem-card">
              <h3>Version Drift</h3>
              <p>Important assumptions get buried across copied files and stale tabs.</p>
            </div>
            <div className="marketing-problem-card">
              <h3>Complexity Overload</h3>
              <p>Understanding someone else's model takes hours or days.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="marketing-solution">
        <div className="marketing-container">
          <div className="marketing-solution-content">
            <div className="marketing-solution-image">
              <img 
                src="/images/Distribution.png" 
                alt="Distribution Visualization - Interactive histogram showing probability distributions" 
                className="marketing-solution-img"
                loading="lazy"
              />
            </div>
            <div className="marketing-solution-text">
              <h2 className="marketing-section-title">See All Possible Outcomes</h2>
              <p className="marketing-section-description">Simcarlo runs thousands of scenarios automatically. See the full range of possibilities, not just best-case or worst-case.</p>
              <ul className="marketing-feature-list">
                <li>Probability distributions, not single values</li>
                <li>Percentiles, confidence intervals, and ranges</li>
                <li>Interactive histograms you can explore</li>
                <li>Identify which inputs matter most</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="marketing-features">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Built for Decision Makers, Not Data Scientists</h2>
          <div className="marketing-features-grid">
            <div className="marketing-feature-card">
              <h3>AI Assistant</h3>
              <p>Tell the AI what you want in plain English. It builds the model for you.</p>
              <div className="marketing-feature-image">
                <img 
                  src="/images/AI Assistant.png" 
                  alt="AI Assistant - Distribution visualizations" 
                  className="marketing-feature-img"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="marketing-feature-card">
              <h3>Instant Results</h3>
              <p>Run thousands of trials in seconds and inspect output ranges as your model changes.</p>
              <div className="marketing-feature-image">
                <img 
                  src="/images/Instant Results.png"  
                  alt="Instant Results - simulation output ranges" 
                  className="marketing-feature-img"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="marketing-feature-card">
              <h3>Time Travel</h3>
              <p>Undo/redo any change. Save versions like Git commits. Compare scenarios side-by-side.</p>
              <div className="marketing-feature-image">
                <img 
                  src="/images/Version History.png" 
                  alt="Version History - Time travel through model versions" 
                  className="marketing-feature-img"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="marketing-feature-card">
              <h3>Export for Review</h3>
              <p>Export results to Excel or save complete simulation models as JSON.</p>
              <div className="marketing-feature-image">
                <img 
                  src="/images/Export.png" 
                  alt="Export Options - Export data and visualizations" 
                  className="marketing-feature-img"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="marketing-how-it-works">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Three Steps to Better Decisions</h2>
          <div className="marketing-steps">
            <div className="marketing-step">
              <div className="marketing-step-number">1</div>
              <div className="marketing-step-content">
                <h3>Define Your Inputs</h3>
                <p>Add distribution nodes for uncertain variables. Choose from normal, triangular, uniform, or fixed values.</p>
                <div className="marketing-step-image">
                  <img 
                    src="/images/Flow - Distribution.png" 
                    alt="Define Your Inputs - Distribution nodes for uncertain variables"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <div className="marketing-step">
              <div className="marketing-step-number">2</div>
              <div className="marketing-step-content">
                <h3>Build Your Model</h3>
                <p>Connect inputs with formulas. The AI can help or use the visual editor. Dependencies update automatically.</p>
                <div className="marketing-step-image">
                  <img 
                    src="/images/Flow - Formula.png" 
                    alt="Build Your Model - Connect inputs with formulas"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            <div className="marketing-step">
              <div className="marketing-step-number">3</div>
              <div className="marketing-step-content">
                <h3>Explore Results</h3>
                <p>Run thousands of scenarios instantly. See distributions, percentiles, and summary stats. Identify what drives outcomes.</p>
                <div className="marketing-step-image">
                  <img 
                    src="/images/Flow - Output.png" 
                    alt="Explore Results - View distributions and statistical summaries"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof
      <section className="marketing-social-proof">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Built for data-driven decisions</h2>
          <div className="marketing-testimonials">
            <div className="marketing-testimonial">
              <div className="marketing-quote">"Finally, a tool that makes Monte Carlo accessible. Our team went from spreadsheet chaos to clear insights in days."</div>
              <div className="marketing-author">
                <div className="marketing-author-image">
                  <div className="marketing-image-placeholder marketing-avatar" title="Dimensions: 60x60px"></div>
                </div>
                <div className="marketing-author-info">
                  <div className="marketing-author-name">Sarah Chen</div>
                  <div className="marketing-author-title">CFO, TechCorp</div>
                </div>
              </div>
            </div>
            <div className="marketing-testimonial">
              <div className="marketing-quote">"The AI assistant is like having a financial analyst on call. It builds models faster than I can describe them."</div>
              <div className="marketing-author">
                <div className="marketing-author-image">
                  <div className="marketing-image-placeholder marketing-avatar" title="Dimensions: 60x60px"></div>
                </div>
                <div className="marketing-author-info">
                  <div className="marketing-author-name">Marcus Rodriguez</div>
                  <div className="marketing-author-title">VP Strategy, GrowthCo</div>
                </div>
              </div>
            </div>
            <div className="marketing-testimonial">
              <div className="marketing-author">
                <div className="marketing-author-image">
                  <div className="marketing-image-placeholder marketing-avatar" title="Dimensions: 60x60px"></div>
                </div>
                <div className="marketing-author-info">
                  <div className="marketing-author-name">Emily Foster</div>
                  <div className="marketing-author-title">Product Manager, DataFlow</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Comparison Table */}
      <section className="marketing-comparison">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Simcarlo vs. Spreadsheets</h2>
          <div className="marketing-comparison-table">
            <div className="marketing-comparison-row marketing-header">
              <div className="marketing-comparison-cell marketing-feature-col">Feature</div>
              <div className="marketing-comparison-cell">Spreadsheets</div>
              <div className="marketing-comparison-cell marketing-highlight">Simcarlo</div>
            </div>
            <div className="marketing-comparison-row">
              <div className="marketing-comparison-cell marketing-feature-col">Handle Uncertainty</div>
              <div className="marketing-comparison-cell">❌ Single values only</div>
              <div className="marketing-comparison-cell marketing-highlight">✅ Full probability distributions</div>
            </div>
            <div className="marketing-comparison-row">
              <div className="marketing-comparison-cell marketing-feature-col">Visual Modeling</div>
              <div className="marketing-comparison-cell">❌ Hidden formulas</div>
              <div className="marketing-comparison-cell marketing-highlight">✅ Clear node graphs</div>
            </div>
            <div className="marketing-comparison-row">
              <div className="marketing-comparison-cell marketing-feature-col">Monte Carlo Simulation</div>
              <div className="marketing-comparison-cell">🔧 Manual add-ins</div>
              <div className="marketing-comparison-cell marketing-highlight">✅ Built-in, one-click</div>
            </div>
            <div className="marketing-comparison-row">
              <div className="marketing-comparison-cell marketing-feature-col">Collaboration</div>
              <div className="marketing-comparison-cell">⚠️ Merge conflicts</div>
              <div className="marketing-comparison-cell marketing-highlight">Version history</div>
            </div>
            <div className="marketing-comparison-row">
              <div className="marketing-comparison-cell marketing-feature-col">AI Assistance</div>
              <div className="marketing-comparison-cell">❌ None</div>
              <div className="marketing-comparison-cell marketing-highlight">✅ Build models with chat</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="marketing-cta">
        <div className="marketing-container">
          <div className="marketing-cta-content">
            <h2 className="marketing-cta-title">Ready to Stop Guessing?</h2>
            <p className="marketing-cta-subtitle">Make better decisions with Simcarlo</p>
            <div className="marketing-cta-buttons">
              <Link to={workspaceLink} className="marketing-btn marketing-btn-primary marketing-btn-large">Open Workspace</Link>
            </div>
            <p className="marketing-cta-note">No credit card • Free forever plan • 5 minute setup</p>
          </div>
        </div>
      </section>
      </main>

      <MarketingFooter />
    </div>
  );
};
